import { iswindow } from "@client/entry";
import { SCRAMJETCLIENT } from "@/symbols";
import { ScramjetClient } from "@client/index";
// import { argdbg } from "@client/shared/err";
import {
	Object_defineProperty,
	Object_getPrototypeOf,
	Object_hasOwn,
} from "@/shared/snapshot";

export function createWrapFn(client: ScramjetClient, self: GlobalThis) {
	let wrappedParent: GlobalThis | null = null;
	let wrappedTop: GlobalThis | null = null;
	if (iswindow) {
		try {
			if (SCRAMJETCLIENT in self.parent) {
				// ... then we're in a subframe, and the parent frame is also in a proxy context, so we should return its proxy
				wrappedParent = self.parent;
			} else {
				// ... then we should pretend we aren't nested and return the current window
				wrappedParent = self;
			}
		} catch {
			// accessing self.parent can throw if it's cross-origin, in which case we should also pretend we aren't nested
			wrappedParent = self;
		}
		// instead of returning top, we need to return the uppermost parent that's inside a scramjet context
		let current = self;
		for (;;) {
			const test = current.parent.self;
			if (test === current) break; // there is no parent, actual or emulated.

			try {
				// ... then `test` represents a window outside of the proxy context, and therefore `current` is the topmost window in the proxy context
				if (!(SCRAMJETCLIENT in test)) break;
			} catch {
				// accessing test can throw if it's cross-origin, in which case we should also break
				break;
			}
			// test is also insde a proxy, so we should continue up the chain
			current = test;
		}
		wrappedTop = current;
	}

	// Every proxied frame has the proxy's origin, so a frame of another site can do what
	// browsers forbid it: send the site's top window elsewhere without a click (ads that
	// hijack the page). The window it reaches through top/parent notes who reached it, until
	// the current task ends; that window's location checks the note (client.checkNavigation).
	const queue = iswindow ? self.queueMicrotask.bind(self) : null;
	const reached = (target: any) => {
		if (target === self) return target;
		try {
			const other: ScramjetClient = target[SCRAMJETCLIENT];
			if (other && other !== client) {
				other.reachedBy = client;
				queue(() => {
					if (other.reachedBy === client) other.reachedBy = null;
				});
			}
		} catch {}

		return target;
	};

	return function (identifier: any) {
		if (identifier === self.location) return client.locationProxy;
		if (identifier === self.eval) {
			return client.indirectEval;
		}
		if (iswindow) {
			if (identifier === self.parent) {
				return reached(wrappedParent);
			} else if (identifier === self.top) {
				return reached(wrappedTop);
			}
		}
		return identifier;
	};
}

function hasNullPrototype(object: any): boolean {
	if (object === null || (typeof object !== "object" && typeof object !== "function"))
		return false;
	try {
		if (Object_getPrototypeOf(object) !== null) return false;
		// a cross-origin window also reports a null prototype, but reading its own properties throws
		Object_hasOwn(object, "location");

		return true;
	} catch {
		return false;
	}
}

export const order = 4;
export default function (client: ScramjetClient, self: GlobalThis) {
	Object_defineProperty(self, client.config.globals.wrapfn, {
		value: client.wrapfn,
		writable: false,
		configurable: false,
		enumerable: false,
	});
	Object_defineProperty(self, client.config.globals.wrappropertyfn, {
		value: function (str, object?) {
			if (
				str === "location" ||
				str === "parent" ||
				str === "top" ||
				str === "eval"
			) {
				// The wrapped key resolves through getters on Object.prototype. An object made with
				// Object.create(null) doesn't have them (and can't be a window), so it keeps the
				// real key. The rewriter only passes the object for `name[key]` and `this[key]`.
				if (arguments.length > 1 && hasNullPrototype(object)) return str;

				return client.config.globals.wrappropertybase + str;
			}

			return str;
		},
		writable: false,
		configurable: false,
		enumerable: false,
	});
	Object_defineProperty(self, client.config.globals.cleanrestfn, {
		value: function (obj) {
			// TODO
		},
		writable: false,
		configurable: false,
		enumerable: false,
	});
	// The rewriter assigns to these two scratch variables (location in destructuring and
	// declarations) without declaring them. In strict code (modules, classes, "use strict")
	// assigning to an undeclared name throws "is not defined" (Transcend's airgap.js on
	// Airtable), so they exist as writable globals from the start.
	for (const name of [
		client.config.globals.templocid,
		client.config.globals.tempunusedid,
	]) {
		Object_defineProperty(self, name, {
			value: undefined,
			writable: true,
			configurable: false,
			enumerable: false,
		});
	}

	Object_defineProperty(
		self.Object.prototype,
		client.config.globals.wrappropertybase + "location",
		{
			get: function () {
				// if (this.location.constructor.toString().includes("Location")) {

				if (this === self || this === self.document) {
					return client.locationProxy;
				}

				return this.location;
			},
			set(value: any) {
				if (this === self || this === self.document) {
					client.url = value;

					return;
				}
				this.location = value;
			},
			configurable: false,
			enumerable: false,
		}
	);
	Object_defineProperty(
		self.Object.prototype,
		client.config.globals.wrappropertybase + "parent",
		{
			get: function () {
				return client.wrapfn(this.parent);
			},
			set(value: any) {
				// i guess??
				this.parent = value;
			},
			configurable: false,
			enumerable: false,
		}
	);
	Object_defineProperty(
		self.Object.prototype,
		client.config.globals.wrappropertybase + "top",
		{
			get: function () {
				return client.wrapfn(this.top);
			},
			set(value: any) {
				this.top = value;
			},
			configurable: false,
			enumerable: false,
		}
	);
	Object_defineProperty(
		self.Object.prototype,
		client.config.globals.wrappropertybase + "eval",
		{
			get: function () {
				return client.wrapfn(this.eval);
			},
			set(value: any) {
				this.eval = value;
			},
			configurable: false,
			enumerable: false,
		}
	);

	self.$scramitize = function (v) {
		const t = typeof v;
		if (t === "object" && v !== null) {
			if (v === location) debugger;
			if (iswindow) {
				// if (v === self.parent) debugger;
				if (v === self.top) debugger;
			}
		} else if (t === "string") {
			if (v.includes("scramjet")) debugger;
			if (v.includes("~/sj")) debugger;
			if (v.includes(location.origin)) debugger;
		}

		return v;
	};

	// location = "..." can't be rewritten as wrapfn(location) = ..., so instead it will actually be rewritten as
	// ((t)=>$scramjet$tryset(location,"+=",t)||location+=t)(...);
	// it has to be a discrete function because there's always the possibility that "location" is a local variable
	// we have to use an IIFE to avoid duplicating side-effects in the getter
	Object_defineProperty(self, client.config.globals.trysetfn, {
		value: function (lhs: any, op: string, rhs: any) {
			if (client.box.locations.has(lhs)) {
				lhs.href = rhs;
				return true;
			}

			return false;
		},
		writable: false,
		configurable: false,
	});
}
